import { useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";
import {
	useUserAssignments,
} from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type {
	MigrationStatus,
	UserAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { UserFlagsToggle } from "./UserFlagsToggle";
import { AssignRoleDrawer } from "./AssignRoleDrawer";
import { AssignWizard } from "./AssignWizard/AssignWizard";

const MIGRATION_BADGE: Record<MigrationStatus, { label: string; className: string }> = {
	migrated: { label: "Migrated", className: "bg-green-100 text-green-700" },
	pending: { label: "Migration pending", className: "bg-amber-100 text-amber-700" },
	review: { label: "Needs review", className: "bg-red-100 text-red-700" },
};

const MigrationBadge = ({ status }: { status: MigrationStatus }) => {
	const badge = MIGRATION_BADGE[status];
	return (
		<span
			className={cn(
				"inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium",
				badge.className
			)}
			data-testid={`migration-badge-${status}`}
		>
			<span className="h-1.5 w-1.5 rounded-full bg-current" />
			{badge.label}
		</span>
	);
};

// AC5: multi-role users show the union of their roles. The effective per-module level is
// server-authoritative (EffectivePermissionsResource); the table lists the assigned roles and
// flags multi-role users as hybrid — it never client-computes a most-restrictive intersection.
const RolePills = ({ user }: { user: UserAssignment }) => (
	<div className="flex flex-wrap items-center gap-1.5">
		{user.assignments.map((assignment) => (
			<span
				key={assignment.assignment_id}
				className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
			>
				{assignment.role_name}
				{assignment.scope_label ? ` · ${assignment.scope_label}` : ""}
			</span>
		))}
		{user.assignments.length > 1 && (
			<span
				className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
				title="Multi-role — effective access is the union (most-permissive)"
				data-testid={`hybrid-badge-${user.id}`}
			>
				hybrid
			</span>
		)}
		{user.assignments.length === 0 && (
			<span className="text-xs text-gray-400">No roles</span>
		)}
	</div>
);

export const UserAssignmentTable = () => {
	const { users, isLoading, error } = useUserAssignments();
	const [wizardOpen, setWizardOpen] = useState(false);
	const [drawerUser, setDrawerUser] = useState<UserAssignment | null>(null);

	if (isLoading) {
		return (
			<div className="flex justify-center py-10" data-testid="users-loading">
				<Spinner />
			</div>
		);
	}

	if (error) {
		return (
			<div className="py-10 text-center text-sm text-red-600" data-testid="users-error">
				Failed to load users.
			</div>
		);
	}

	return (
		<div data-testid="user-assignment-table">
			<div className="mb-4 flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Users &amp; Roles</h2>
					<p className="text-sm text-gray-500">
						{users.length} user{users.length === 1 ? "" : "s"}
					</p>
				</div>
				<Button
					btntype="primary"
					size="sm"
					onClick={() => setWizardOpen(true)}
					data-testid="open-assign-wizard"
				>
					<FiUsers className="mr-1.5 inline" size={16} /> Assign template
				</Button>
			</div>

			<div className="overflow-x-auto rounded-lg border border-gray-200">
				<table className="w-full border-collapse text-sm">
					<thead>
						<tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
							<th className="px-3 py-2 font-semibold">User</th>
							<th className="px-3 py-2 font-semibold">Roles</th>
							<th className="px-3 py-2 font-semibold">Migration status</th>
							<th className="px-3 py-2 font-semibold">Template creation</th>
							<th className="px-3 py-2" />
						</tr>
					</thead>
					<tbody>
						{users.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-3 py-8 text-center text-gray-400" data-testid="users-empty">
									No users found.
								</td>
							</tr>
						) : (
							users.map((user) => (
								<tr key={user.id} className="border-t border-gray-100" data-testid={`user-row-${user.id}`}>
									<td className="px-3 py-2">
										<div className="font-semibold">{user.name}</div>
										<div className="text-xs text-gray-500">{user.email}</div>
									</td>
									<td className="px-3 py-2">
										<RolePills user={user} />
									</td>
									<td className="px-3 py-2">
										<MigrationBadge status={user.migration_status} />
									</td>
									<td className="px-3 py-2">
										<UserFlagsToggle user={user} />
									</td>
									<td className="px-3 py-2 text-right">
										<Button
											btntype="plain-border"
											size="xs"
											onClick={() => setDrawerUser(user)}
											data-testid={`manage-user-${user.id}`}
										>
											Manage
										</Button>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			<AssignWizard
				isOpen={wizardOpen}
				onClose={() => setWizardOpen(false)}
				users={users}
			/>

			<AssignRoleDrawer
				isOpen={drawerUser !== null}
				onClose={() => setDrawerUser(null)}
				user={drawerUser}
			/>
		</div>
	);
};
